import React, { useState, useEffect } from 'react';
import { Card, Space, Typography, Alert, Button, Tooltip, DatePicker } from 'antd';
import { EnhancedPRStatsTable } from './components/EnhancedPRStatsTable';
import type { PRStatsWithKey } from './types';
import { fetchPRStats } from '../../api/github';
import { ReloadOutlined } from '@ant-design/icons';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

export const PRStatsPage: React.FC = () => {
	const [statsLoading, setStatsLoading] = useState(false);
	const [stats, setStats] = useState<PRStatsWithKey[]>([]);
	const [error, setError] = useState<{ message: string; type: 'error' | 'warning' } | null>(null);
	const [isFromCache, setIsFromCache] = useState(false);
	const [isDummyData, setIsDummyData] = useState(false);
	const [rateLimit, setRateLimit] = useState<{ remaining: string; reset: string } | null>(null);
	const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([
		dayjs().subtract(2, 'weeks'),
		dayjs(),
	]);

	const fetchStats = async (forceRefresh = false) => {
		setStatsLoading(true);
		setError(null);
		setIsDummyData(false);

		try {
			const params: { startDate?: string; endDate?: string } = {};
			if (dateRange) {
				params.startDate = dateRange[0].toISOString();
				params.endDate = dateRange[1].toISOString();
			}

			const response = await fetchPRStats(params, forceRefresh, '');

			console.log('DEBUG: Received stats response:', response);

			const statsWithKeys: PRStatsWithKey[] = response.data.map((stat) => ({
				...stat,
				key: stat.author,
			}));

			setIsFromCache(response.fromCache);
			setIsDummyData(response.isDummyData || false);
			if (response.rateLimit) {
				setRateLimit({
					remaining: response.rateLimit.remaining,
					reset: response.rateLimit.resetDate,
				});
			}

			setStats(statsWithKeys);
		} catch (error: any) {
			console.error('DEBUG: Error fetching PR stats:', error);
			setError({
				message: error.message || 'Failed to fetch PR statistics',
				type: 'error',
			});
		} finally {
			setStatsLoading(false);
		}
	};

	useEffect(() => {
		fetchStats();
	}, []);

	const handleDateRangeChange = (dates: RangePickerProps['value']) => {
		setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
		fetchStats(true);
	};

	const handleRefresh = () => {
		fetchStats(true);
	};

	return (
		<>
			<Card className="pr-stats-container">
				<Space direction="vertical" size="large" style={{ width: '100%' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Title level={4}>Pull Request Statistics</Title>
						<Space>
							<RangePicker value={dateRange} onChange={handleDateRangeChange} allowClear />
							<Tooltip title="Refresh data">
								<Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={statsLoading} />
							</Tooltip>
						</Space>
					</div>

					{error && (
						<Alert
							message={error.message}
							type={error.type}
							showIcon
							closable
							onClose={() => setError(null)}
						/>
					)}

					{isFromCache && (
						<Alert
							message="Using cached data"
							type="info"
							showIcon
							action={
								<Button size="small" type="link" onClick={() => fetchStats(true)}>
									Refresh
								</Button>
							}
						/>
					)}

					{isDummyData && (
						<Alert message="Using dummy data for development" type="warning" showIcon />
					)}

					{rateLimit && Number(rateLimit.remaining) === 0 && !isDummyData && (
						<Alert
							message="GitHub API Rate Limit Reached"
							description={`The rate limit will reset at ${new Date(rateLimit.reset).toLocaleString()}`}
							type="warning"
							showIcon
						/>
					)}

					<EnhancedPRStatsTable data={stats} loading={statsLoading} dateRange={dateRange} />
				</Space>
			</Card>
		</>
	);
};
